import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import useThemeColor from "../../hooks/useThemeColor";
import "./zReport.css";
import "../order/ManageOrderFilters.css";

const reconId = (row) => row.stockReconciliation_ID ?? row.stockReconciliationId ?? row.StockReconciliation_ID;

const ReconciliationReportNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedCompID, setSelectedCompID] = useState(searchParams.get("compId") || "");

  const toInt = (value) => {
    const n = typeof value === "number" ? value : parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  };

  React.useEffect(() => {
    const baseCompId = toInt(CompID);
    if (baseCompId && (!selectedCompID || Number(selectedCompID) <= 0)) {
      setSelectedCompID(String(baseCompId));
    }
  }, [CompID, selectedCompID]);

  React.useEffect(() => {
    const fetchLocations = async () => {
      if (!isMain || !CompID) return;
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
        const locationData = response.data?.data ?? response.data ?? [];
        const arr = Array.isArray(locationData) ? locationData : [];
        const baseCompId = toInt(CompID);
        if (!baseCompId) return;
        const parentName = profileData?.compName ?? profileData?.CompName ?? `Company ${baseCompId}`;
        const base = [{ value: baseCompId, label: parentName }];
        const branches = arr
          .filter((loc) => {
            const id = toInt(loc.CompID ?? loc.compID);
            const pid = toInt(loc.ParentComID ?? loc.parentComID);
            return id && pid === baseCompId && id !== baseCompId;
          })
          .map((loc) => {
            const id = toInt(loc.CompID ?? loc.compID);
            const name = String(loc.CompName ?? loc.compName ?? "").trim();
            return { value: id, label: name || `Branch ${id}` };
          })
          .filter((loc) => loc.value);
        setLocations([...base, ...branches]);
      } catch {
        setLocations([]);
      }
    };
    fetchLocations();
  }, [CompID, isMain, profileData]);

  const effectiveCompID = isMain ? Number(selectedCompID) || 0 : Number(CompID) || 0;

  const btnStyle = {
    backgroundColor: themeColor,
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: `0 2px 8px ${themeColor}40`,
  };

  const create = async () => {
    if (!effectiveCompID) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post(APIs.POST_STOCK_RECONCILIATION_CREATE, {
        compID: effectiveCompID,
        notes: name?.trim() || null,
      });
      if (res.data?.status && res.data.data) {
        showSuccessToast("Reconciliation created");
        const id = reconId(res.data.data);
        if (id) navigate(`/report/reconciliationReport/${id}?compId=${effectiveCompID}`);
        else navigate("/report/reconciliationReport");
      } else {
        showErrorToast(res.data?.message || "Create failed");
      }
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Create failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/report/reconciliationReport">Stock Adjustments</Link>
            </li>
            <li className="breadcrumb-item active">New</li>
          </ol>
          <h1 className="page-header mb-0">Add reconciliation</h1>
        </div>
        <div className="ms-auto">
          <Link to="/report/reconciliationReport" className="btn btn-outline-secondary">
            Back to list
          </Link>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div
          className="card-body p-4"
          style={{
            borderRadius: "12px",
            border: `1px solid ${themeColor}33`,
            background: `linear-gradient(180deg, #fff 0%, #f8fafb 100%)`,
          }}
        >
          <p className="text-muted mb-4">
            Enter a name for this count session (optional). You will enter physical stock on the next screen.
          </p>
          {isMain && (
            <div className="mb-3">
              <label className="form-label fw-semibold">Target branch</label>
              <select
                className="form-control form-control-lg"
                value={selectedCompID}
                onChange={(e) => setSelectedCompID(e.target.value)}
                style={{ maxWidth: 560 }}
              >
                {locations.map((loc) => (
                  <option key={loc.value} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="mb-4">
            <label className="form-label fw-semibold">Reconciliation name</label>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="e.g. Weekly kitchen count — April 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ maxWidth: 560 }}
            />
          </div>
          <button type="button" className="btn px-4" style={btnStyle} onClick={create}>
            <i className="fa fa-arrow-right me-2" />
            Continue to line items
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationReportNew;
